import { AxiosError } from 'axios';
import { useCallback } from 'react';
import { useHistory } from 'react-router';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';
import CODE_TYPE_ENUM from '@api/requests/auth/enums/CODE_TYPE_ENUM.enum';

import { showToast } from '@app/providers/ToastProvider';
import { authActions } from '@app/redux/auth/auth.reducer';
import { getAuthFormData } from '@app/redux/authForms/authForms.selectors';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import RoutePaths from '@app/routes/paths';
import useGenerateRoutePath from '@app/routes/paths/useGenerateRoutePath';

import { ResetMethodFormValues } from '@components/forms/resetPasswordMethodForm/interfaces';

function useResetPasswordCode(type: CODE_TYPE_ENUM) {
	const history = useHistory();
	const generateRoutePath = useGenerateRoutePath();
	const dispatch = useAppDispatch();
	const authFormData = useAppSelector(getAuthFormData);

	const { startRequest } = useTriggeredRequest(AccountClientRequests.Auth.sendResetPasswordCode);

	const initialValues: ResetMethodFormValues = {
		email: undefined,
		phone: {
			code: undefined,
			number: undefined,
		},
		code_type: undefined,
		reset_type: authFormData.reset_password_type,
		auth_form_slug: authFormData.slug,
	};

	const onSubmit = useCallback(
		async (values: ResetMethodFormValues) => {
			try {
				const isEmailCodeType = type === CODE_TYPE_ENUM.EMAIL;
				const contact = isEmailCodeType ? values.email : `${values.phone.code || ''}${values.phone.number || ''}`;

				await startRequest({
					auth_form_slug: values.auth_form_slug,
					contact,
					code_type: isEmailCodeType ? CODE_TYPE_ENUM.EMAIL : CODE_TYPE_ENUM.SMS,
					reset_type: values.reset_type,
				});

				dispatch(
					authActions.setPasswordReset({
						auth_form_slug: values.auth_form_slug,
						contact,
						code_type: isEmailCodeType ? CODE_TYPE_ENUM.EMAIL : CODE_TYPE_ENUM.SMS,
						reset_type: values.reset_type,
					}),
				);
			} catch (e) {
				const { response } = e as AxiosError<{
					errors?: {
						description?: string;
						field?: string;
					}[];
				}>;

				const fallbackMessage = 'Oops. Something went wrong.';

				const errorDescription = response?.data?.errors?.[0]?.description || fallbackMessage;

				showToast.error(errorDescription);
			}

			history.push(generateRoutePath(RoutePaths.AUTH.RESET_PASSWORD));
		},
		[startRequest, type, dispatch, history, generateRoutePath],
	);

	return {
		initialValues,
		onSubmit,
	};
}

export default useResetPasswordCode;
